import { Link, Stack } from "@mui/material";
import { Card, Typography } from "@platform/design-system";

import { CensusUnreadableValues } from "./CensusUnreadableValues";

import type {
  CensusSourceDetail,
  CensusValidation,
} from "../hooks/useCensusValidation";

interface CensusReadingReportProps {
  readonly validation: CensusValidation;
}

const Section = ({
  title,
  explanation,
  children,
}: {
  readonly title: string;
  readonly explanation: string;
  readonly children: React.ReactNode;
}): React.JSX.Element => (
  <Stack gap={0.5}>
    <Typography variant="subtitle2">{title}</Typography>
    <Typography variant="caption" color="textSecondary">
      {explanation}
    </Typography>
    {children}
  </Stack>
);

/**
 * The file a census was read from.
 *
 * A name with no link is the ordinary outcome for a document that has since
 * been deleted or flagged: the ingestion records the name itself, so it
 * outlives the document and still says which file was read. Nothing renders
 * for a census that read no file.
 */
const SourceFile = ({
  source,
}: {
  readonly source: CensusSourceDetail;
}): React.JSX.Element | null => {
  if (!source.filename) {
    return null;
  }

  if (!source.documentUrl) {
    return (
      <Typography variant="body-medium" data-testid="census-source-file">
        {source.filename}
      </Typography>
    );
  }

  return (
    <Typography variant="body-medium">
      <Link
        href={source.documentUrl}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="census-source-file-link"
      >
        {source.filename}
      </Link>
    </Typography>
  );
};

/**
 * What reading the census could not place.
 *
 * This sits beside the checklist rather than below it because the two answer
 * different questions. The checklist says a field is missing; this says
 * whether the census carried it under a heading we do not know. A field
 * reported missing next to a heading listed here is ours to fix, not the
 * broker's.
 */
export const CensusReadingReport = ({
  validation,
}: CensusReadingReportProps): React.JSX.Element => {
  const coercionFailures = Object.entries(validation.coercionFailures ?? {});
  const { source } = validation;

  return (
    <Card sx={{ p: 2 }} data-testid="census-reading-report">
      <Stack gap={2}>
        <Typography variant="subtitle1">How the census was read</Typography>

        {source && (
          <Section
            title="Source"
            explanation="Where the census came from and how it was read."
          >
            <Typography variant="body-medium">
              {source.source} · {source.outcome}
              {source.sheetName ? ` · sheet "${source.sheetName}"` : ""}
              {source.headerRow ? ` · header row ${source.headerRow}` : ""}
            </Typography>
            <SourceFile source={source} />
          </Section>
        )}

        <Section
          title="Members"
          explanation="Only employees are checked against the checklist. A carrier is quoted on employees."
        >
          <Typography variant="body-medium">
            {validation.employees} of {validation.members} members are employees
          </Typography>
        </Section>

        {coercionFailures.length > 0 && (
          <Section
            title="Values we could not read"
            explanation="Values we found in a column we read and could not understand, and the rows they sit on. Dates of birth, zips and salaries are shown by row only."
          >
            <CensusUnreadableValues
              counts={validation.coercionFailures ?? {}}
              columns={validation.unreadableValues ?? []}
            />
          </Section>
        )}
      </Stack>
    </Card>
  );
};
